import React from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withRouter, RouteComponentProps } from 'react-router';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import DataGrid from 'devextreme-react/data-grid';
import styles from './GridWidget.styles';
import Typography from '@material-ui/core/Typography';
import { Paper } from '@material-ui/core';
import { PaperElevation } from 'utils/constants';
import { useRetrieveProgramGridLines, useRetrieveColumnHyperlink } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { WIDGET_TYPES, PROGRAM } from 'components/common/Dashboard/Dashboard.constants';

const ROW_HEIGHT = 34;
const HEADER_HEIGHT = 40;
const DEFAULT_MAX_DISPLAY_ROWS = 10;

export interface IGridWidgetProps extends WithStyles<typeof styles>, RouteComponentProps {
  dataSource?: any[];
  columns: any[];
  maxHeight?: number;
  showHeading?: boolean;
  headingText?: string;
  card?: any;
  programId?: string;
  selectedCustomerId: number;
  selectedProductId: number;
}

const GridWidget: React.FC<IGridWidgetProps> = ({
  classes,
  history,
  columns,
  showHeading = true,
  headingText = '',
  card,
  programId,
  selectedCustomerId,
  selectedProductId
}) => {

  const isCustomerEnquiry = programId === PROGRAM.CUSTOMER_ENQUIRY && !!selectedCustomerId;
  const isInventoryEnquiry = programId === PROGRAM.INVENTORY_ENQUIRY && !!selectedProductId;

  if (!card || !programId || (!isCustomerEnquiry && !isInventoryEnquiry)) {
    return null;
  }

  const widgetName = React.useMemo(() => WIDGET_TYPES[card?.WidgetId]?.name || '', [card?.WidgetId]);

  const program = React.useMemo(
    () => ({
      name: programId === PROGRAM.CUSTOMER_ENQUIRY ? 'customer' : programId === PROGRAM.INVENTORY_ENQUIRY ? 'inventory' : 'pickslips',
      id: programId === PROGRAM.CUSTOMER_ENQUIRY ? selectedCustomerId : programId === PROGRAM.INVENTORY_ENQUIRY ? selectedProductId : 0,
      key: programId === PROGRAM.CUSTOMER_ENQUIRY ? 'CustomerId' : programId === PROGRAM.INVENTORY_ENQUIRY ? 'ProductId' : 'DispatchId',
      grid: widgetName
    }),
    [programId, selectedCustomerId, card?.WidgetId, widgetName]
  );

  const fetchLinesMutation = useRetrieveProgramGridLines(program);
  const fetchHyperlinksMutation = useRetrieveColumnHyperlink();
  const dataGridRef = React.useRef<DataGrid>();

  const updatedColumns = React.useMemo(
    () => {
      const firstRow = fetchLinesMutation.data?.GridDataSchema?.GridData?.[0];

      const mappedColumns = columns.map((col) => {
        const baseColumn = {
          ...col,
          dataField: firstRow?.[col.Name]?.hasOwnProperty('Display') ? `${col.Name}Display` : col.Name,
          caption: col.HasHeader ? col.Label : '',
          allowFiltering: false,
          alignment: col.Alignment || 'left',
        };

        if (col.HasHyperlink) {
          baseColumn.cellRender = (cellInfo) => {
            const rowData = cellInfo?.data || {};

            return (
              <a
                href='#'
                onClick={async (e) => {
                  e.preventDefault();
                  const response = await fetchHyperlinksMutation.mutateAsync({
                    WidgetId: card?.WidgetId,
                    FieldName: col.Name,
                    Key1: rowData[`${col.Name}Key1`] || '',
                    Key2: rowData[`${col.Name}Key2`] || '',
                  });
                  if (response?.Status && response?.Hyperlink) {
                    history.push(`/${response?.Hyperlink}`);
                  }
                }}
                className={classes.hyperlink}
              >
                {cellInfo.value}
              </a>
            );
          };
        }

        return baseColumn;
      });

      return [
        ...mappedColumns,
        {
          dataField: '__empty__',
          caption: '',
          allowFiltering: false,
          allowSorting: false,
          width: 50,
          cellRender: () => null,
        }
      ];
    },
    [columns, fetchLinesMutation.data, fetchHyperlinksMutation, card?.WidgetId, history]
  );

  const fetchLines = React.useCallback(
    async (BatchPage, BatchSize, Sort, filter) => {
        if (Number(card?.DashboardWidgetId) > 0 && program?.id) {
            try {
                const lines = await fetchLinesMutation.mutateAsync({
                  DashboardWidgetId: card?.DashboardWidgetId,
                  [program.key]: program.id,
                  ...filter,
                  urlQueryParams: {
                      BatchPage,
                      BatchSize,
                      Sort
                  }
                });

                return lines?.GridData;
            } catch (error) {
                console.error('Error fetching lines:', error);

                return null;
            }
        } else {
            return null;
        }
    },
    [card, selectedCustomerId, selectedProductId, program?.id, program?.key]
  );

  const linesDataSource = React.useMemo(
    () => createDataSource(
      'Sequence',
      { fetch: fetchLines }
    ),
    [fetchLines, selectedCustomerId, selectedProductId]
  );

  return (
    <Paper className={classes.root} elevation={PaperElevation}>
      {showHeading && headingText && (
        <Typography variant='h6' className={classes.heading}>
          {headingText}
        </Typography>
      )}
      {
        program?.id && (isCustomerEnquiry || isInventoryEnquiry) && (
          <DynamicDataGrid
            gridProps={{
              dataGridRef: dataGridRef,
              dataSource: linesDataSource,
              height: card?.MaxDisplayRow * ROW_HEIGHT + HEADER_HEIGHT, // Max rows + header height
              width: '100%',
              style: { maxHeight: card?.MaxDisplayRow * ROW_HEIGHT + HEADER_HEIGHT || DEFAULT_MAX_DISPLAY_ROWS * ROW_HEIGHT + HEADER_HEIGHT }
            }}
            columns={updatedColumns || columns}
            dataGridClassName={classes.dataGrid}
            allowHeaderFilting={false}
            allowDelete={false}
            allowUpdating={false}
            actionButtonsVisibility={false}
          />
        )
      }
    </Paper>
  );
};

export default withRouter(withStyles(styles)(GridWidget));
